// File: src/components/forms/Select/Select.tsx

import React from "react";
import styled from "styled-components";

interface Option {
  label: string;
  value: string;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: Option[];
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  fullWidth?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const Wrapper = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
`;

const StyledLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled.select<{ fullWidth?: boolean }>`
  appearance: none;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  background-color: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.rounded};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ theme }) => theme.elevations.low};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  /* 👇 iOS-friendly size tap target on mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.md};
    font-size: 16px;
    border-radius: ${({ theme }) => theme.radii.md};
  }
`;

const Chevron = styled.span`
  position: absolute;
  right: ${({ theme }) => theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none; /* Use native dropdown icon on mobile */
  }
`;

export const Select: React.FC<SelectProps> = ({
  label,
  options,
  value = "",
  onChange,
  fullWidth = true,
  disabled = false,
  placeholder,
  ...rest
}) => {
  return (
    <Wrapper fullWidth={fullWidth}>
      {label && <StyledLabel>{label}</StyledLabel>}
      <SelectContainer>
        <StyledSelect
          value={value}
          onChange={onChange}
          disabled={disabled}
          fullWidth={fullWidth}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </StyledSelect>
        <Chevron>▾</Chevron>
      </SelectContainer>
    </Wrapper>
  );
};